import React, {Component} from 'react';
import {connect} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as A from '../store/lineageActions';
import {normalizeImport} from '../util/importLineage';

const INITIAL_STATE = {
  file: null,
  serverRows: null,
  serverErrors: [],
  skipInvalid: true,
  uploading: false,
  error: null,
};

class ImportModal extends Component {
  constructor(props) {
    super(props);
    this.state = {...INITIAL_STATE};
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      this.setState({...INITIAL_STATE});
    }
  }

  async _upload() {
    const {file} = this.state;
    if (!file) return;
    this.setState({uploading: true, error: null, serverRows: null, serverErrors: []});
    try {
      const rows = await this.props.uploadImportFile(file);
      const serverRows = Array.isArray(rows) ? rows : (rows && rows.rows ? rows.rows : []);
      const serverErrors = (rows && rows.errors) ? rows.errors : [];
      this.setState({serverRows, serverErrors, uploading: false});
    } catch (e) {
      this.setState({uploading: false, error: e.message || 'Upload failed'});
    }
  }

  _doImport() {
    const {serverRows, skipInvalid} = this.state;
    const result = normalizeImport(serverRows, this.props.map, {skipInvalid});
    this.props.importLineage(result.payload);
    this.props.onClose();
    const {summary} = result;
    const parts = [`Imported ${summary.nodeCount} nodes · ${summary.edgeCount} connections · ${summary.mappingCount} field mappings`];
    if (summary.skippedCount > 0) parts.push(`${summary.skippedCount} rows skipped`);
    this.props.notify(parts.join(' · '), 'Undo', () => this.props.undo());
  }

  render() {
    const {open, onClose} = this.props;
    const {file, serverRows, serverErrors, skipInvalid, uploading, error} = this.state;

    let preview = null;
    let allErrors = [...serverErrors];
    if (serverRows) {
      preview = normalizeImport(serverRows, this.props.map, {skipInvalid});
      allErrors = [...serverErrors, ...preview.errors];
    }

    const noAdditions = preview &&
      (preview.summary.nodeCount + preview.summary.edgeCount + preview.summary.mappingCount) === 0;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="import-modal">
        <DialogTitle>Import lineage from Excel</DialogTitle>
        <DialogContent dividers>
          <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16}}>
            <input
              type="file"
              accept=".xlsx"
              data-testid="import-file-input"
              onChange={(e) => this.setState({file: e.target.files[0] || null, serverRows: null, serverErrors: [], error: null})}
            />
            <Button
              size="small"
              onClick={() => this.props.downloadTemplateFile()}
              data-testid="import-download-template-btn"
            >
              Download template
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!file || uploading}
              onClick={() => this._upload()}
              data-testid="import-upload-btn"
            >
              {uploading ? <CircularProgress size={16} /> : 'Upload & preview'}
            </Button>
          </div>

          {error && (
            <Typography variant="body2" style={{color: '#c62828', marginBottom: 8}} data-testid="import-upload-error">
              {error}
            </Typography>
          )}

          {preview && (
            <>
              <Typography variant="body2" style={{marginBottom: 8}} data-testid="import-summary">
                {`Will add ${preview.summary.nodeCount} nodes · ${preview.summary.edgeCount} connections · ${preview.summary.mappingCount} field mappings`}
                {preview.summary.skippedCount > 0 ? ` · ${preview.summary.skippedCount} rows skipped` : ''}
              </Typography>

              {allErrors.length > 0 && (
                <div
                  style={{maxHeight: 180, overflowY: 'auto', marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4, padding: '4px 8px'}}
                  data-testid="import-errors"
                >
                  {allErrors.map((err, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      display="block"
                      style={{color: err.level === 'error' ? '#c62828' : '#e65100'}}
                    >
                      {`Row ${err.row}: ${err.msg}`}
                    </Typography>
                  ))}
                </div>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipInvalid}
                    onChange={(e) => this.setState({skipInvalid: e.target.checked})}
                    color="primary"
                    inputProps={{'data-testid': 'import-skip-invalid'}}
                  />
                }
                label="Skip rows with errors and import the rest"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} data-testid="import-cancel-btn">Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={uploading || !preview || noAdditions}
            onClick={() => this._doImport()}
            data-testid="import-confirm-btn"
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = (state) => ({
  map: state.lineageReducer.map,
});

const mapDispatchToProps = {
  importLineage: A.importLineage,
  uploadImportFile: A.uploadImportFile,
  downloadTemplateFile: A.downloadTemplateFile,
  notify: A.notify,
  undo: A.undo,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImportModal);
